'use client';

import { useFocusRing } from '@react-aria/focus';
import { useHover, usePress } from '@react-aria/interactions';

import { mergeProps } from '../../../utils/mergeProps';

interface UseMenuItemInteractionProps {
  isDisabled: boolean;
}

/**
 * Shared interaction hook for all menu item types. Wraps `useFocusRing`,
 * `useHover`, and `usePress` with consistent config and returns merged
 * props + state for data attributes.
 */
function useMenuItemInteraction({ isDisabled }: UseMenuItemInteractionProps) {
  const { focusProps, isFocusVisible, isFocused } = useFocusRing();
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { pressProps, isPressed } = usePress({ isDisabled });

  const interactionProps = mergeProps(focusProps, hoverProps, pressProps, {
    'data-hovered': isHovered || undefined,
    'data-focused': isFocused || undefined,
    'data-focus-visible': isFocusVisible || undefined,
    'data-pressed': isPressed || undefined,
    'data-disabled': isDisabled || undefined,
  });

  return { interactionProps };
}

export { useMenuItemInteraction };
